import { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, ImagePlus, Loader2, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;
const api = axios.create({ baseURL: API_URL, withCredentials: true });

const PRESETS = [
  { headline: 'Gifting, minus the guesswork', subline: 'Mementa learns who they are and finds gifts that feel hand-picked.' },
  { headline: 'The perfect gift exists. We find it.', subline: 'Curated gift discovery for the people you love.' },
  { headline: 'Never panic-buy a gift again', subline: 'Thoughtful, personal gift ideas — matched by Mementa.' },
  { headline: 'For the friend who has everything', subline: 'Mementa uncovers gifts they would never think to ask for.' },
];

const TONES = [
  { key: 'heartfelt', label: 'Heartfelt' },
  { key: 'playful', label: 'Playful' },
  { key: 'minimal', label: 'Minimal' },
];

const SIZES = [
  { key: 'square', label: 'Square', dims: '1080×1080' },
  { key: 'portrait', label: 'Portrait', dims: '1080×1350' },
  { key: 'story', label: 'Story / Reel', dims: '1080×1920' },
  { key: 'landscape', label: 'Landscape', dims: '1200×628' },
];

export default function BrandAdBuilder({ theme, ThemePicker }) {
  const [headline, setHeadline] = useState(PRESETS[0].headline);
  const [subline, setSubline] = useState(PRESETS[0].subline);
  const [tone, setTone] = useState('heartfelt');
  const [aiLoading, setAiLoading] = useState(false);
  const [shots, setShots] = useState([]);
  const [shot, setShot] = useState('');
  const [customShot, setCustomShot] = useState(null); // {key, preview}
  const [uploadingShot, setUploadingShot] = useState(false);
  const fileInputRef = useRef(null);
  const [previewSize, setPreviewSize] = useState('square');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [checkedSizes, setCheckedSizes] = useState(() => new Set(SIZES.map((s) => s.key)));
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState('');
  const urlRef = useRef(null);

  useEffect(() => {
    api.get('/admin/ads/feature-shots').then((res) => setShots(res.data)).catch(() => {});
  }, []);

  // debounced preview
  useEffect(() => {
    if (!headline.trim()) { setPreviewUrl(null); return; }
    let cancelled = false;
    setPreviewLoading(true);
    const timer = setTimeout(() => {
      api.post('/admin/ads/render-card', { kind: 'brand', title: headline, subtitle: subline, size: previewSize, theme, shot }, { responseType: 'blob' })
        .then((res) => {
          if (cancelled) return;
          if (urlRef.current) URL.revokeObjectURL(urlRef.current);
          const url = URL.createObjectURL(res.data);
          urlRef.current = url;
          setPreviewUrl(url);
        })
        .catch(() => !cancelled && toast.error('Preview failed'))
        .finally(() => !cancelled && setPreviewLoading(false));
    }, 900);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [headline, subline, previewSize, theme, shot]);

  const uploadCustomShot = async (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Please pick an image file');
    if (file.size > 8 * 1024 * 1024) return toast.error('Image is over 8MB');
    setUploadingShot(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await api.post('/admin/ads/upload-shot', fd);
      if (customShot?.preview) URL.revokeObjectURL(customShot.preview);
      const preview = URL.createObjectURL(file);
      setCustomShot({ key: res.data.shot, preview });
      setShot(res.data.shot);
      toast.success('Image added — rendering your ad');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingShot(false);
    }
  };

  const generateCopy = async () => {
    setAiLoading(true);
    try {
      const res = await api.post('/admin/ads/brand-copy', { tone });
      setHeadline(res.data.headline);
      setSubline(res.data.subline);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'AI copy failed');
    } finally {
      setAiLoading(false);
    }
  };

  const toggleSize = (key) => {
    setCheckedSizes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const downloadSelected = async () => {
    if (!headline.trim() || checkedSizes.size === 0) return;
    setDownloading(true);
    try {
      const sizes = SIZES.filter((s) => checkedSizes.has(s.key));
      for (let i = 0; i < sizes.length; i += 1) {
        setProgress(`Rendering ${sizes[i].label} (${i + 1}/${sizes.length})…`);
        const res = await api.post('/admin/ads/render-card', { kind: 'brand', title: headline, subtitle: subline, size: sizes[i].key, theme, shot }, { responseType: 'blob' });
        const disposition = res.headers['content-disposition'] || '';
        const match = disposition.match(/filename="(.+)"/);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(res.data);
        a.download = match ? match[1] : `mementa-brand-${sizes[i].key}.png`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(a.href), 5000);
      }
      toast.success(`${sizes.length} brand ad${sizes.length > 1 ? 's' : ''} downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Download failed');
    } finally {
      setDownloading(false);
      setProgress('');
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6" data-testid="brand-ad-builder">
      {/* Left: message */}
      <div className="lg:col-span-2 space-y-4">
        <div className="rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-2" style={{ color: '#9E6752' }}>Start from a preset</p>
          <div className="space-y-1.5">
            {PRESETS.map((p, i) => (
              <button
                key={p.headline}
                onClick={() => { setHeadline(p.headline); setSubline(p.subline); }}
                data-testid={`brand-preset-${i}`}
                className="w-full text-left rounded-lg px-3 py-2 transition-colors hover:bg-[#FFF8F0]"
                style={{ border: `1px solid ${headline === p.headline ? '#FED7A5' : '#F3F4F6'}`, background: headline === p.headline ? '#FFF8F0' : 'white' }}
              >
                <span className="block text-xs font-semibold" style={{ color: '#20212B' }}>{p.headline}</span>
                <span className="block text-[11px]" style={{ color: '#9E6752' }}>{p.subline}</span>
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white border p-4 space-y-3" style={{ borderColor: '#E5E0DA' }}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <p className="text-xs font-semibold uppercase tracking-wider flex items-center gap-1.5" style={{ color: '#9E6752' }}>
              <Sparkles size={13} /> Or write with AI
            </p>
            <div className="flex items-center gap-1.5">
              {TONES.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setTone(t.key)}
                  data-testid={`brand-tone-${t.key}`}
                  className="px-2 py-1 rounded-full text-[11px] font-semibold transition-all"
                  style={{
                    background: tone === t.key ? '#9E6752' : '#FFF8F0',
                    color: tone === t.key ? '#FFFAF5' : '#534145',
                    border: '1px solid #9E675233',
                  }}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
          <Button size="sm" onClick={generateCopy} disabled={aiLoading} data-testid="brand-ai-generate-btn" className="text-white" style={{ background: '#9E6752' }}>
            {aiLoading ? <><Loader2 size={14} className="animate-spin mr-1.5" /> Writing…</> : <><Sparkles size={14} className="mr-1.5" /> Generate copy</>}
          </Button>
          <div>
            <label className="text-xs font-semibold" style={{ color: '#534145' }}>Headline</label>
            <Input value={headline} onChange={(e) => setHeadline(e.target.value)} data-testid="brand-headline-input" />
          </div>
          <div>
            <label className="text-xs font-semibold" style={{ color: '#534145' }}>Subline</label>
            <Input value={subline} onChange={(e) => setSubline(e.target.value)} data-testid="brand-subline-input" />
          </div>
        </div>

        {/* Feature shot picker */}
        {shots.length > 0 && (
          <div className="rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }} data-testid="brand-shot-card">
            <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: '#9E6752' }}>Feature shot</p>
            <p className="text-[11px] mb-3" style={{ color: '#9E6752' }}>
              Show the app in action — a real screenshot inside a phone mockup, or upload your own image.
            </p>
            <div className="grid grid-cols-3 gap-2">
              <button
                onClick={() => setShot('')}
                data-testid="brand-shot-none"
                className="rounded-xl flex items-center justify-center text-[11px] font-semibold transition-all"
                style={{
                  aspectRatio: '9 / 14',
                  border: `2px solid ${shot === '' ? '#9E6752' : '#E5E0DA'}`,
                  background: '#FFF8F0',
                  color: '#534145',
                }}
              >
                Text only
              </button>
              {shots.map((s) => (
                <button
                  key={s.key}
                  onClick={() => setShot(s.key)}
                  data-testid={`brand-shot-${s.key}`}
                  title={s.label}
                  className="rounded-xl overflow-hidden relative transition-all"
                  style={{
                    aspectRatio: '9 / 14',
                    border: `2px solid ${shot === s.key ? '#9E6752' : '#E5E0DA'}`,
                    opacity: shot === s.key || shot === '' ? 1 : 0.65,
                  }}
                >
                  <img
                    src={`${API_URL}/admin/ads/feature-shots/${s.key}.png`}
                    alt={s.label}
                    loading="lazy"
                    className="absolute inset-0 w-full h-full object-cover object-top"
                  />
                  <span
                    className="absolute bottom-0 left-0 right-0 text-[9px] font-semibold px-1 py-0.5 truncate"
                    style={{ background: 'rgba(83,65,69,0.85)', color: '#FFFAF5' }}
                  >
                    {s.label}
                  </span>
                </button>
              ))}
              {/* Upload your own */}
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingShot}
                data-testid="brand-shot-upload"
                title="Upload your own image"
                className="rounded-xl overflow-hidden relative flex flex-col items-center justify-center gap-1 transition-all"
                style={{
                  aspectRatio: '9 / 14',
                  border: `2px ${customShot ? 'solid' : 'dashed'} ${shot === customShot?.key ? '#9E6752' : '#D4A4A8'}`,
                  background: '#FFF8F0',
                  color: '#534145',
                  opacity: shot === '' || shot === customShot?.key ? 1 : 0.65,
                }}
              >
                {customShot ? (
                  <>
                    <img
                      src={customShot.preview}
                      alt="Your upload"
                      className="absolute inset-0 w-full h-full object-cover object-top"
                      onClick={(e) => { e.stopPropagation(); setShot(customShot.key); }}
                    />
                    <span
                      className="absolute bottom-0 left-0 right-0 text-[9px] font-semibold px-1 py-0.5 truncate"
                      style={{ background: 'rgba(83,65,69,0.85)', color: '#FFFAF5' }}
                    >
                      Your upload — click to replace
                    </span>
                  </>
                ) : uploadingShot ? (
                  <Loader2 size={18} className="animate-spin" style={{ color: '#9E6752' }} />
                ) : (
                  <>
                    <ImagePlus size={18} style={{ color: '#9E6752' }} />
                    <span className="text-[10px] font-semibold text-center px-1">Upload your own</span>
                  </>
                )}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              data-testid="brand-shot-file-input"
              onChange={(e) => { uploadCustomShot(e.target.files?.[0]); e.target.value = ''; }}
            />
          </div>
        )}
      </div>

      {/* Right: preview + export */}
      <div className="lg:col-span-3 space-y-4">
        <div className="rounded-2xl bg-white border p-4 flex flex-wrap items-center gap-4" style={{ borderColor: '#E5E0DA' }}>
          {ThemePicker}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: '#9E6752' }}>Preview</span>
            {SIZES.map((s) => (
              <button
                key={s.key}
                onClick={() => setPreviewSize(s.key)}
                data-testid={`brand-preview-size-${s.key}`}
                className="px-2.5 py-1.5 rounded-full text-xs font-semibold transition-all"
                style={{
                  background: previewSize === s.key ? '#9E6752' : '#FFF8F0',
                  color: previewSize === s.key ? '#FFFAF5' : '#534145',
                  border: '1px solid #9E675233',
                }}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>

        <div className="rounded-2xl bg-white border p-4 flex items-center justify-center" style={{ borderColor: '#E5E0DA', minHeight: 320 }} data-testid="brand-preview-panel">
          {previewLoading ? (
            <div className="flex flex-col items-center gap-2 py-16">
              <Loader2 size={26} className="animate-spin" style={{ color: '#9E6752' }} />
              <p className="text-xs" style={{ color: '#9E6752' }}>Rendering preview…</p>
            </div>
          ) : previewUrl ? (
            <img src={previewUrl} alt="Brand ad preview" data-testid="brand-preview-image" className="rounded-xl shadow-md" style={{ maxHeight: 440, maxWidth: '100%', objectFit: 'contain' }} />
          ) : null}
        </div>

        <div className="rounded-2xl bg-white border p-4" style={{ borderColor: '#E5E0DA' }}>
          <p className="text-xs font-semibold uppercase tracking-wider mb-3" style={{ color: '#9E6752' }}>Export sizes</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
            {SIZES.map((s) => (
              <label
                key={s.key}
                className="flex items-start gap-2 p-2.5 rounded-xl cursor-pointer transition-colors"
                style={{
                  background: checkedSizes.has(s.key) ? '#FFF8F0' : 'white',
                  border: `1px solid ${checkedSizes.has(s.key) ? '#FED7A5' : '#E5E0DA'}`,
                }}
              >
                <input type="checkbox" checked={checkedSizes.has(s.key)} onChange={() => toggleSize(s.key)} data-testid={`brand-size-check-${s.key}`} className="mt-0.5" />
                <span>
                  <span className="block text-xs font-semibold" style={{ color: '#20212B' }}>{s.label}</span>
                  <span className="block text-[10px]" style={{ color: '#9E6752' }}>{s.dims}</span>
                </span>
              </label>
            ))}
          </div>
          <Button
            onClick={downloadSelected}
            disabled={downloading || checkedSizes.size === 0 || !headline.trim()}
            data-testid="brand-download-btn"
            className="text-white"
            style={{ background: '#534145' }}
          >
            {downloading ? (
              <><Loader2 size={15} className="animate-spin mr-2" /> {progress}</>
            ) : (
              <><Download size={15} className="mr-2" /> Download {checkedSizes.size} size{checkedSizes.size !== 1 ? 's' : ''} ({theme})</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
